// Fee & Obligation tab — the loan-scoped B3 review queue. The same
// approve/waive mechanics as the cross-loan /portfolio-admin/fee-review page
// (useFeeReviewQueue with a loanId filter — the backend param already
// exists), rendered as a section so B3 can act without leaving the loan.
// Self-gates on Fees.Review: without the permission the section renders
// nothing (other roles see the Fees panel below instead).

import { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  GlossaryText,
  Skeleton,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency, formatCalendarDate } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import {
  FEE_REVIEW_SOURCE_TYPES,
  type FeeReviewItemDto,
} from "@/api/portfolioAdmin/loanFeesApi";
import {
  useApproveFee,
  useFeeReviewQueue,
  useWaiveFee,
} from "@/hooks/portfolioAdmin/useLoanFees";

const SOURCE_LABEL: Record<string, string> = Object.fromEntries(
  FEE_REVIEW_SOURCE_TYPES.map((s) => [s.value, s.label]),
);

export function LoanFeeReviewSection({ loanId }: { loanId: string }) {
  const { hasPermission } = useAuth();
  const canReview = hasPermission("Fees.Review");

  const toast = useToast();
  const query = useFeeReviewQueue({ status: "pending_review", loanId }, canReview);
  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  const approve = useApproveFee();
  const waive = useWaiveFee();

  const [waiveTarget, setWaiveTarget] = useState<FeeReviewItemDto | null>(null);
  const [waiveReason, setWaiveReason] = useState("");

  if (!canReview) return null;

  const onApprove = async (item: FeeReviewItemDto) => {
    try {
      await approve.mutateAsync({ feeId: item.id, loanId: item.loanId });
      toast.success("Fee approved — it bills on the next statement");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const confirmWaive = async () => {
    if (!waiveTarget || waiveReason.trim().length === 0) return;
    try {
      await waive.mutateAsync({
        feeId: waiveTarget.id,
        loanId: waiveTarget.loanId,
        reason: waiveReason.trim(),
      });
      toast.success("Fee waived");
      setWaiveTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid="loan-fee-review-section">
      <CardHeader>
        <CardTitle>Fee review</CardTitle>
        <CardDescription>
          Fees on this loan awaiting a B3 decision. Approving creates the
          waterfall obligation and bills the fee on the next statement; waiving
          requires a reason and never reaches the ladder.
        </CardDescription>
      </CardHeader>

      {query.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : query.isError ? (
        <p
          className="text-small text-status-error"
          role="alert"
          data-testid="loan-fee-review-error"
        >
          Couldn't load the review queue. Try again.
        </p>
      ) : items.length === 0 ? (
        <p className="text-small text-gray-mid" data-testid="loan-fee-review-empty">
          Nothing awaiting review on this loan.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Fee</TH>
              <TH className="text-right">Amount</TH>
              <TH>Period</TH>
              <TH>Why it was assessed</TH>
              <TH>
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {items.map((item) => (
              <TR key={item.id} data-testid={`loan-fee-review-row-${item.id}`}>
                <TD>
                  <div>
                    <GlossaryText text={item.feeType} />
                  </div>
                  <div className="text-caption text-gray-mid">
                    {SOURCE_LABEL[item.sourceType] ?? item.sourceType}
                  </div>
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(item.amount)}
                </TD>
                <TD className="text-gray-mid whitespace-nowrap">
                  {item.periodStart
                    ? formatCalendarDate(item.periodStart)
                    : formatCalendarDate(item.incurredDate)}
                </TD>
                <TD className="max-w-reading">
                  {item.triggerDescription ? (
                    <GlossaryText text={item.triggerDescription} />
                  ) : (
                    "—"
                  )}
                </TD>
                <TD>
                  <div className="flex justify-end gap-space-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => void onApprove(item)}
                      disabled={approve.isPending}
                      data-testid={`loan-fee-review-approve-${item.id}`}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setWaiveReason("");
                        setWaiveTarget(item);
                      }}
                      data-testid={`loan-fee-review-waive-${item.id}`}
                    >
                      Waive
                    </Button>
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <Modal
        open={waiveTarget !== null}
        onClose={() => setWaiveTarget(null)}
        title="Waive this fee"
        description={
          waiveTarget
            ? `${SOURCE_LABEL[waiveTarget.sourceType] ?? waiveTarget.feeType} of ${formatCurrency(waiveTarget.amount)}. The fee will not bill; the waiver is audited.`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setWaiveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void confirmWaive()}
              disabled={waiveReason.trim().length === 0}
              loading={waive.isPending}
              data-testid="loan-fee-review-waive-confirm"
            >
              Waive fee
            </Button>
          </>
        }
      >
        <label
          htmlFor="loan-fee-waive-reason"
          className="font-display text-caption uppercase tracking-label text-gray-mid"
        >
          Reason for waiving (required)
        </label>
        <textarea
          id="loan-fee-waive-reason"
          className="mt-1 w-full min-h-[88px] border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
          value={waiveReason}
          onChange={(e) => setWaiveReason(e.target.value)}
          data-testid="loan-fee-waive-reason"
        />
      </Modal>
    </Card>
  );
}
